import { useState, useEffect } from 'react';
import { settingsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Settings, Save, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function NetSuiteSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState({
    account_id: '',
    consumer_key: '',
    consumer_secret: '',
    token_id: '',
    token_secret: '',
    restlet_url: '',
  });
  const [showSecrets, setShowSecrets] = useState({
    consumer_key: false,
    consumer_secret: false,
    token_id: false,
    token_secret: false,
  });
  const [source, setSource] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getNetSuite();
      setCredentials({
        account_id: response.data.account_id,
        consumer_key: response.data.consumer_key,
        consumer_secret: response.data.consumer_secret,
        token_id: response.data.token_id,
        token_secret: response.data.token_secret,
        restlet_url: response.data.restlet_url || '',
      });
      setSource(response.data.source);
    } catch (error) {
      toast.error('Failed to load NetSuite credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setCredentials({ ...credentials, [field]: value });
    setIsDirty(true);
  };

  const toggleShowSecret = (field) => {
    setShowSecrets({ ...showSecrets, [field]: !showSecrets[field] });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.updateNetSuite(credentials);
      toast.success('NetSuite credentials updated successfully');
      setIsDirty(false);
      fetchCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchCredentials();
    setIsDirty(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="netsuite-settings">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="h-8 w-8 text-sky-600" />
          <h2 className="text-3xl font-bold tracking-tight">NetSuite Settings</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Configure NetSuite API credentials for work order synchronization
        </p>
      </div>

      <div className="max-w-3xl">
        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>API Credentials</CardTitle>
                <CardDescription>
                  Manage your NetSuite REST API authentication credentials
                </CardDescription>
              </div>
              <Badge variant={source === 'database' ? 'default' : 'secondary'}>
                {source === 'database' ? 'Custom Config' : 'Environment Default'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Account ID */}
            <div className="space-y-2">
              <Label htmlFor="account-id">
                Account ID
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Input
                id="account-id"
                data-testid="account-id-input"
                value={credentials.account_id}
                onChange={(e) => handleChange('account_id', e.target.value)}
                placeholder="Enter NetSuite Account ID"
              />
              <p className="text-xs text-muted-foreground">
                Your NetSuite account number (e.g., 4246957)
              </p>
            </div>

            {/* Consumer Key */}
            <div className="space-y-2">
              <Label htmlFor="consumer-key">
                Consumer Key / Client ID
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="consumer-key"
                  data-testid="consumer-key-input"
                  type={showSecrets.consumer_key ? 'text' : 'password'}
                  value={credentials.consumer_key}
                  onChange={(e) => handleChange('consumer_key', e.target.value)}
                  placeholder="Enter Consumer Key"
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleShowSecret('consumer_key')}
                  data-testid="toggle-consumer-key"
                >
                  {showSecrets.consumer_key ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Consumer Secret */}
            <div className="space-y-2">
              <Label htmlFor="consumer-secret">
                Consumer Secret / Client Secret
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="consumer-secret"
                  data-testid="consumer-secret-input"
                  type={showSecrets.consumer_secret ? 'text' : 'password'}
                  value={credentials.consumer_secret}
                  onChange={(e) => handleChange('consumer_secret', e.target.value)}
                  placeholder="Enter Consumer Secret"
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleShowSecret('consumer_secret')}
                  data-testid="toggle-consumer-secret"
                >
                  {showSecrets.consumer_secret ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Token ID */}
            <div className="space-y-2">
              <Label htmlFor="token-id">
                Token ID
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="token-id"
                  data-testid="token-id-input"
                  type={showSecrets.token_id ? 'text' : 'password'}
                  value={credentials.token_id}
                  onChange={(e) => handleChange('token_id', e.target.value)}
                  placeholder="Enter Token ID"
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleShowSecret('token_id')}
                  data-testid="toggle-token-id"
                >
                  {showSecrets.token_id ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Token Secret */}
            <div className="space-y-2">
              <Label htmlFor="token-secret">
                Token Secret
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="token-secret"
                  data-testid="token-secret-input"
                  type={showSecrets.token_secret ? 'text' : 'password'}
                  value={credentials.token_secret}
                  onChange={(e) => handleChange('token_secret', e.target.value)}
                  placeholder="Enter Token Secret"
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => toggleShowSecret('token_secret')}
                  data-testid="toggle-token-secret"
                >
                  {showSecrets.token_secret ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* RESTlet URL */}
            <div className="space-y-2 pt-4 border-t border-border">
              <Label htmlFor="restlet-url">
                RESTlet URL (Optional - Recommended)
              </Label>
              <Input
                id="restlet-url"
                data-testid="restlet-url-input"
                value={credentials.restlet_url}
                onChange={(e) => handleChange('restlet_url', e.target.value)}
                placeholder="https://4246957.restlets.api.netsuite.com/app/site/hosting/restlet.nl?script=XXX&deploy=1"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                If you deployed a custom RESTlet, enter its External URL here. RESTlets provide more reliable access than standard REST API.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!isDirty || saving}
                data-testid="reset-button"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isDirty || saving}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="save-credentials-button"
              >
                {saving ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Credentials
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 shadow-sm bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <h4 className="font-bold text-sm mb-2 text-blue-900 dark:text-blue-100">
              Where to find your NetSuite credentials:
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>1. Log in to NetSuite as an Administrator</li>
              <li>2. Go to Setup → Integration → Manage Integrations</li>
              <li>3. Create a new integration or select existing one</li>
              <li>4. Copy the Consumer Key and Consumer Secret</li>
              <li>5. Go to Setup → Users/Roles → Access Tokens</li>
              <li>6. Create a token for your integration and copy Token ID and Token Secret</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
